import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, Clock, Award, Play, ChevronRight, 
  GraduationCap
} from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All Courses" },
  { id: "beginner", label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" },
];

const FEATURED_WORKSHOPS = [
  {
    id: "workshop-1",
    title: "The Laughing Drama Therapist Workshop",
    description: "Interactive workshop using dramatic arts for emotional healing and personal growth led by Mr. RAPsoul.R",
    duration: "2 hours",
    type: "Live Workshop",
    image: "https://images.unsplash.com/photo-1733222765234-08c33d436c50?w=600",
    price: 75
  },
  {
    id: "workshop-2",
    title: "SMAHo.ARTS Therapeutic Drama",
    description: "Transformative group sessions using dramatic performance for behavioral change",
    duration: "90 min",
    type: "Group Session",
    image: "https://images.unsplash.com/photo-1685609239980-b38a6fceabd1?w=600",
    price: 50
  }
];

const BOOK_SERIES = [
  "Book 1: Awakening the Mind - Understanding Relapse",
  "Book 2: Deeper Dive into Triggers",
  "Book 3: Rethinking Your Reward Pathway",
  "Book 4: Emotional Regulation",
  "Book 5: Challenging Maladaptive Thoughts",
  "Book 6: Building a Sustainable Lifestyle"
];

const LEVEL_COLORS = {
  Beginner: 'bg-green-100 text-green-700',
  Intermediate: 'bg-yellow-100 text-yellow-700',
  Advanced: 'bg-red-100 text-red-700',
  'All Levels': 'bg-blue-100 text-blue-700'
};

export default function LifeSkills() {
  const [courses, setCourses] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");

  const fetchCourses = useCallback(async () => {
    try {
      const response = await apiClient.get('/content/life-skills');
      setCourses(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const filteredCourses = activeCategory === "all" 
    ? courses 
    : courses.filter(c => c.level?.toLowerCase() === activeCategory);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="life-skills-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">SMAHoo Change Clinic & Academy</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            Life Skills Academy
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Develop essential life skills through courses, workshops, and training programs 
            focused on emotional resilience, behavioral change, and personal growth.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-spa-terracotta text-white'
                  : 'bg-spa-muted text-spa-text-secondary hover:bg-spa-border'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Featured Workshops */}
        <div className="grid md:grid-cols-2 gap-6">
          {FEATURED_WORKSHOPS.map((workshop) => (
            <div 
              key={workshop.id}
              className="spa-card overflow-hidden group"
              data-testid={`workshop-${workshop.id}`}
            >
              <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden">
                <img 
                  src={workshop.image}
                  alt={workshop.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-spa-terracotta text-white text-xs px-3 py-1 rounded-full">
                    {workshop.type}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="font-serif text-xl">{workshop.title}</p>
                </div>
              </div>
              <p className="text-spa-text-secondary mb-4">{workshop.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-spa-text-secondary">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {workshop.duration}
                  </span>
                </div>
                <Button className="spa-button-primary px-6">
                  CA${workshop.price} - Enroll
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Courses Grid */}
        <div>
          <h2 className="font-serif text-2xl text-spa-moss mb-6">Self-Paced Courses</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <div 
                key={course.id}
                className="spa-card group cursor-pointer"
                data-testid={`course-${course.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-spa-sage/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-spa-terracotta/10 transition-colors">
                    <BookOpen className="w-6 h-6 text-spa-moss" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        LEVEL_COLORS[course.level] || LEVEL_COLORS['All Levels']
                      }`}>
                        {course.level}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg text-spa-moss mb-1">{course.title}</h3>
                    <p className="text-sm text-spa-text-secondary line-clamp-2 mb-3">
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-spa-text-secondary">
                        <Clock className="w-4 h-4" /> {course.duration}
                      </span>
                      <span className="flex items-center gap-1 text-spa-terracotta font-medium">
                        <Award className="w-4 h-4" /> +{course.coins_reward} coins
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-spa-border">
                  <Button className="w-full spa-button-secondary text-sm">
                    <Play className="w-4 h-4 mr-2" /> Start Course
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Book Series */}
        <div className="spa-card bg-gradient-to-br from-spa-muted to-white">
          <div className="flex items-center gap-3 mb-6">
            <GraduationCap className="w-6 h-6 text-spa-terracotta" />
            <h2 className="font-serif text-2xl text-spa-moss">Behavioral Change Book Series</h2>
          </div>
          <p className="text-spa-text-secondary mb-6">
            Mr. RAPsoul.R's latest six-book series on behavioral change and stress management:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {BOOK_SERIES.map((book, index) => (
              <div 
                key={book}
                className="p-4 bg-white rounded-xl border border-spa-border flex items-center gap-3"
              >
                <div className="w-8 h-8 bg-spa-terracotta/10 rounded-lg flex items-center justify-center text-spa-terracotta font-serif">
                  {index + 1}
                </div>
                <p className="text-sm text-spa-moss flex-1">{book.split(': ')[1]}</p>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <a 
              href="https://www.amazon.com/dp/B0FHMLX5WY" 
              target="_blank" 
              rel="noopener noreferrer"
              className="spa-button-primary inline-flex items-center gap-2"
            >
              View on Amazon <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
